import React, { useState, useEffect } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import PopupMessage from "../../components/common/PopUpMessage";
import "../../assets/css/GmailSetup.css";
import EmailService from "../../services/EmailService";

const EmailSetup = () => {
  const [form, setForm] = useState({
    smtpServer: "",
    senderEmail: "",
    smtpPort: "",
    userName: "",
    senderName: "",
    password: "",
  });

  const [selectedProvider, setSelectedProvider] = useState(null);
  const [token, setToken] = useState(null);

  const [compose, setCompose] = useState({
    to: "",
    subject: "",
    body: "",
  });

  // ✅ Common popup state
  const [popup, setPopup] = useState({ message: "", type: "success" });

  // ✅ On mount, check localStorage for Gmail token
  useEffect(() => {
    const token = EmailService.getStoredGmailToken();
    if (token) {
      setToken(token);
      setSelectedProvider("Gmail");
    }
  }, []);

  // ✅ Google login hook
  const google_login = useGoogleLogin({
    scope:
      "https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.readonly",
    onSuccess: async (response) => {
      const emailAuth = {
        emailtype: "gmail",
        access_token: response.access_token,
      };
      localStorage.setItem("email_auth", JSON.stringify(emailAuth));
      setSelectedProvider("Gmail");
      setToken(response.access_token);
      setPopup({ message: "Gmail integrated successfully!", type: "success" });

      try {
        const userInfo = await EmailService.getGoogleUserInfo(response.access_token);
        console.log("User Info:", userInfo.data);
      } catch (error) {
        console.error("Error fetching user info:", error);
      }
    },
    onError: (error) => {
      console.log("OAuth Error:", error);
      setPopup({ message: "Failed to integrate Gmail.", type: "error" });
    },
  });

  // ✅ Handle provider selection
  const handleProviderClick = (provider) => {
    setSelectedProvider(provider);

    // clear compose form whenever switching provider
    setCompose({ to: "", subject: "", body: "" });

    if (provider === "Gmail") {
      if (token) {
        setPopup({
          message: "Gmail is already integrated and live.",
          type: "success",
        });
      } else {
        google_login();
      }
    }
  };

  // ✅ Handle SMTP form changes
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // ✅ Handle SMTP form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("SMTP Credentials Submitted:", form);
    setPopup({ message: "SMTP credentials saved!", type: "success" });
  };

  // ✅ Send Email function
  const handleSendEmail = async () => {
    if (!compose.to || !compose.subject || !compose.body) {
      setPopup({
        message: "Please fill in all fields before sending.",
        type: "error",
      });
      return;
    }

    if (selectedProvider === "Gmail") {
      if (!token) {
        setPopup({ message: "Please login with Gmail first.", type: "error" });
        return;
      }

      try {
        const response = await EmailService.sendGmail(
          token,
          compose.to,
          compose.subject,
          compose.body
        );

        console.log("Email sent:", response.data);
        setPopup({ message: "Email sent successfully!", type: "success" });

        // ✅ clear form after send
        setCompose({ to: "", subject: "", body: "" });
      } catch (error) {
        console.error("Error sending email:", error);
        setPopup({ message: "Failed to send email.", type: "error" });
      }
    } else {
      // Placeholder for Outlook/Other send logic
      console.log("Send via:", selectedProvider, compose);
      setPopup({
        message: `${selectedProvider} email sent (mock)!`,
        type: "success",
      });

      // ✅ clear form after send
      setCompose({ to: "", subject: "", body: "" });
    }
  };

  return (
    <div className="mail-setup-wrapper">
      <h2 className="section-title">Integrate your mail</h2>
      <p className="section-subtitle">
        Send emails through your business email account to your leads.
      </p>

      {/* Mail Providers */}
      <div className="mail-providers">
        {["Gmail", "Outlook", "Other"].map((provider) => (
          <div
            className={`provider-item ${
              selectedProvider === provider ? "selected" : ""
            } ${provider === "Gmail" && token ? "active-green" : ""}`}
            key={provider}
            onClick={() => handleProviderClick(provider)}
          >
            <div className="provider-circle">
              <img
                src={`../assets/images/${provider.toLowerCase()}.png`}
                alt={provider}
                style={{ maxHeight: "40px" }}
              />
            </div>
            <span>{provider}</span>
          </div>
        ))}
      </div>

      {/* SMTP Form (only for Other) */}
      {selectedProvider === "Other" && (
        <form className="smtp-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            <input
              type="text"
              name="smtpServer"
              placeholder="SMTP server"
              value={form.smtpServer}
              onChange={handleChange}
            />
            <input
              type="email"
              name="senderEmail"
              placeholder="Sender email"
              value={form.senderEmail}
              onChange={handleChange}
            />
            <input
              type="text"
              name="smtpPort"
              placeholder="SMTP port"
              value={form.smtpPort}
              onChange={handleChange}
            />
            <input
              type="text"
              name="userName"
              placeholder="User name"
              value={form.userName}
              onChange={handleChange}
            />
            <input
              type="text"
              name="senderName"
              placeholder="Sender name"
              value={form.senderName}
              onChange={handleChange}
            />
            <input
              type="password"
              name="password"
              placeholder="Password"
              value={form.password}
              onChange={handleChange}
            />
          </div>
          <div className="form-submit">
            <button type="submit">Save Credentials</button>
          </div>
        </form>
      )}

      {/* ✅ Compose Email (shown for all providers) */}
      {selectedProvider && (
        <div className="compose-email-section">
          <div className="compose-header">
            <h3>Compose Emails ({selectedProvider})</h3>
            <button className="all-leads-btn">Send to All leads</button>
          </div>

          <div className="compose-box">
            <div className="compose-to">
              <label>To:</label>
              <input
                type="text"
                placeholder="Enter recipient"
                value={compose.to}
                onChange={(e) =>
                  setCompose({ ...compose, to: e.target.value })
                }
              />
            </div>

            <div className="compose-to">
              <label>Subject:</label>
              <input
                type="text"
                placeholder="Enter subject"
                value={compose.subject}
                onChange={(e) =>
                  setCompose({ ...compose, subject: e.target.value })
                }
              />
            </div>

            <div className="compose-to">
              <label>Message:</label>
              <textarea
                rows="8"
                placeholder="Enter your message"
                value={compose.body}
                onChange={(e) =>
                  setCompose({ ...compose, body: e.target.value })
                }
              ></textarea>
            </div>

            <button className="send-btn" onClick={handleSendEmail}>
              Send
            </button>
          </div>
        </div>
      )}

      {/* ✅ Common PopupMessage */}
      <PopupMessage
        message={popup.message}
        type={popup.type}
        onClose={() => setPopup({ message: "", type: "success" })}
      />
    </div>
  );
};

export default EmailSetup;
